import {
  InfoOutlined,
  LocationOnOutlined,
  WorkOutlineOutlined,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Chip,
  Container,
  Paper,
  Skeleton,
  Typography,
  useTheme
} from '@mui/material';
import { where } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { useMicrospecDynamicUIContext } from '../../MicrospecDynamicUI/GlobalProvider';
import {
  getDataFromCollectionWithQuery,
  getDocById,
  getPaginatedData,
} from '../../MicrospecDynamicUI/Helpers/dataUtils';
import PagesLayout from '../components/PagesLayout';
import { PropertyCard, PropertyCardSkeleton } from '../components/PropertyCard';

const PROPERTIES_PER_PAGE = 9;

// ─── Owners Public Page ──────────────────────────────────────────────────────
const OwnersPublicPage = () => {
  const { ownerId } = useParams();
  const theme = useTheme();
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { firestore } = cloudProvider;

  const [owner, setOwner] = useState(null);
  const [ownerLoading, setOwnerLoading] = useState(true);

  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);

  const observerRef = useRef(null);

  // Load owner
  useEffect(() => {
    if (!firestore?.instance || !ownerId) return;
    getDocById(setOwner, (retrieved) => { if (retrieved) setOwnerLoading(false); }, firestore.instance, 'users', ownerId, false);
  }, [firestore?.instance, ownerId]);

  // Load owner's properties
  useEffect(() => {
    if (!firestore?.instance || !ownerId) return;
    setPropertiesLoading(true);
    setProperties([]);
    setLastDoc(null);
    setHasMore(true);

    const wheres = [
      where('owner_id', '==', ownerId),
      where('active', '==', true),
      where('approval_status', '==', 'approved'),
    ];

    getDataFromCollectionWithQuery(
      (data) => {
        setProperties(data);
        if (data.length > 0) setLastDoc(data[data.length - 1]);
        setHasMore(data.length >= PROPERTIES_PER_PAGE);
      },
      (retrieved) => { if (retrieved) setPropertiesLoading(false); },
      firestore.instance,
      'properties',
      wheres,
      false,
      PROPERTIES_PER_PAGE
    );
  }, [firestore?.instance, ownerId]);

  // Load more
  const loadMore = useCallback(() => {
    if (!firestore?.instance || !lastDoc || loadingMore) return;
    setLoadingMore(true);

    const wheres = [
      where('owner_id', '==', ownerId),
      where('active', '==', true),
      where('approval_status', '==', 'approved'),
    ];

    getPaginatedData(lastDoc, PROPERTIES_PER_PAGE, 'properties', firestore, 'desc', 'updated_at', wheres)
      .then((data) => {
        setProperties((prev) => [...prev, ...data]);
        if (data.length > 0) setLastDoc(data[data.length - 1]);
        setHasMore(data.length >= PROPERTIES_PER_PAGE);
        setLoadingMore(false);
      });
  }, [firestore, lastDoc, loadingMore, ownerId]);

  // Infinite scroll observer
  const lastPropertyRef = useCallback(
    (node) => {
      if (loadingMore) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) loadMore();
      });
      if (node) observerRef.current.observe(node);
    },
    [loadingMore, hasMore, loadMore]
  );

  const fullName = owner
    ? `${owner.first_name || ''} ${owner.last_name || ''}`.trim()
    : '';

  // Loading state
  if (ownerLoading) {
    return (
      <PagesLayout>
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            pt: 8,
            pb: 12,
          }}
        >
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Skeleton variant="circular" width={120} height={120} sx={{ mx: 'auto', mb: 2 }} />
            <Skeleton variant="text" width={200} height={36} sx={{ mx: 'auto' }} />
            <Skeleton variant="text" width={140} height={20} sx={{ mx: 'auto', mt: 1 }} />
          </Container>
        </Box>
      </PagesLayout>
    );
  }

  // Not found
  if (!owner) {
    return (
      <PagesLayout>
        <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            المستخدم غير موجود
          </Typography>
          <Typography color="text.secondary">
            قد يكون الحساب قد أزيل أو أن الرابط غير صحيح
          </Typography>
        </Container>
      </PagesLayout>
    );
  }

  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  const seoTitle = fullName
    ? `${fullName} | عقاركو`
    : 'عقاركو - منصة العقارات';
  const seoDescription = owner.about
    ? owner.about.slice(0, 160)
    : `عرض عقارات ${fullName} على عقاركو${owner.address ? ' - ' + owner.address : ''}`;
  const seoImage = owner.photo_url || '';

  return (
    <PagesLayout>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={pageUrl} />
        {seoImage && <meta property="og:image" content={seoImage} />}
        <meta property="og:locale" content="ar_SA" />
        <meta property="og:site_name" content="عقاركو" />

        {/* Twitter Card */}
        <meta name="twitter:card" content={seoImage ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        {seoImage && <meta name="twitter:image" content={seoImage} />}
      </Helmet>

      {/* Header Banner */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light} 100%)`,
          pt: 8,
          pb: 6,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.06,
            backgroundImage: `radial-gradient(circle at 30% 30%, #fff 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}
        />
        <Container
          maxWidth="md"
          sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}
        >
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Avatar
              src={owner.photo_url}
              alt={fullName}
              sx={{
                width: 120,
                height: 120,
                mx: 'auto',
                mb: 2,
                border: '4px solid rgba(255,255,255,0.3)',
                fontSize: '2.5rem',
                bgcolor: 'rgba(255,255,255,0.2)',
                color: '#fff',
              }}
            >
              {fullName.charAt(0)}
            </Avatar>
            <Typography variant="h4" fontWeight={800} sx={{ color: '#fff' }}>
              {fullName}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                mt: 1.5,
                flexWrap: 'wrap',
              }}
            >
              {owner.years_of_experience > 0 && (
                <Chip
                  icon={<WorkOutlineOutlined sx={{ color: 'rgba(255,255,255,0.8) !important' }} />}
                  label={`${owner.years_of_experience} سنوات خبرة`}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    fontWeight: 600,
                    backdropFilter: 'blur(4px)',
                  }}
                />
              )}
              {owner.address && (
                <Chip
                  icon={<LocationOnOutlined sx={{ color: 'rgba(255,255,255,0.8) !important' }} />}
                  label={owner.address}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    fontWeight: 600,
                    backdropFilter: 'blur(4px)',
                  }}
                />
              )}
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ mt: 3, position: 'relative', zIndex: 2, pb: 6 }}>
        {/* About Section */}
        {owner.about && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                p: 3,
                mb: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <InfoOutlined color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>
                  نبذة عن المعلن
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                {owner.about}
              </Typography>
            </Paper>
          </motion.div>
        )}

        {/* Properties */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
            عقارات المعلن
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 3,
            }}
          >
            {propertiesLoading
              ? [...Array(6)].map((_, i) => <PropertyCardSkeleton key={i} />)
              : properties.map((property, index) => (
                  <Box
                    key={property.id}
                    ref={index === properties.length - 1 ? lastPropertyRef : null}
                  >
                    <PropertyCard property={property} />
                  </Box>
                ))}

            {loadingMore &&
              [...Array(3)].map((_, i) => <PropertyCardSkeleton key={`loading-${i}`} />)}
          </Box>

          {!propertiesLoading && !loadingMore && properties.length === 0 && (
            <Paper
              elevation={0}
              sx={{
                textAlign: 'center',
                py: 6,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" color="text.secondary">
                لا توجد عقارات منشورة حالياً
              </Typography>
            </Paper>
          )}
        </motion.div>
      </Container>
    </PagesLayout>
  );
};

export default OwnersPublicPage;
